import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import RichTextEditor from "../../components/forms/RichTextEditor";

interface EditInitiativeProps {}

interface InitiativeFormData {
  // Tab 1: Thông tin sáng kiến
  initiativeName: string;
  managingUnit: string;
  proposer: string;
  implementationMonth: string;
  researchField: string;
  year: string;
  estimatedBudget: string;
  status: string;

  // Tab 2: Nội dung đầy đủ
  summary: string;
  applicationCapability: string;
  applyingUnit: string;
  expectedOutcome: string;
  attachedFiles: File[];
  fullContent: string;
}

const EditInitiative: React.FC<EditInitiativeProps> = () => {
  const navigate = useNavigate();

  // Get ID from URL path
  const getInitiativeId = () => {
    const pathParts = window.location.pathname.split("/");
    return pathParts[pathParts.length - 1];
  };

  const id = getInitiativeId();

  const [activeTab, setActiveTab] = useState("initiative-info");
  const [formData, setFormData] = useState<InitiativeFormData>({
    initiativeName: "",
    managingUnit: "",
    proposer: "",
    implementationMonth: "",
    researchField: "",
    year: "",
    estimatedBudget: "",
    status: "",
    summary: "",
    applicationCapability: "",
    applyingUnit: "",
    expectedOutcome: "",
    attachedFiles: [],
    fullContent: "",
  });

  // Mock data for editing - in real app, this would come from API
  const mockInitiativeData: InitiativeFormData = {
    initiativeName: "Sáng kiến 1 »",
    managingUnit: "Ban Tổng Giám đốc",
    proposer: "Thượng tá, VS. ThS Lê Anh Tuấn",
    implementationMonth: "2 Tháng",
    researchField: "Chiến lược thủy lợi của các vùng, miền và quốc gia",
    year: "2024",
    estimatedBudget: "50000000",
    status: "Mới",
    summary: "Sáng kiến về chiến lược thủy lợi hiện đại cho các vùng miền",
    applicationCapability: "Có thể áp dụng rộng rãi trong các dự án thủy lợi",
    applyingUnit: "Các đơn vị thủy lợi trên toàn quốc",
    expectedOutcome: "Nâng cao hiệu quả quản lý thủy lợi",
    attachedFiles: [],
    fullContent: "<p>Nội dung đầy đủ của sáng kiến kinh nghiệm...</p>",
  };

  useEffect(() => {
    // Load initiative data for editing
    if (id) {
      // In real app, fetch data from API based on id
      setFormData(mockInitiativeData);
    }
  }, [id]);

  const handleInputChange = (
    field: keyof InitiativeFormData,
    value: string | File | File[] | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value as never,
    }));
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      handleInputChange("attachedFiles", [
        ...formData.attachedFiles,
        ...newFiles,
      ]);
    }
  };

  const handleSave = () => {
    console.log("Updating initiative data:", formData);
    // TODO: Implement update functionality
    navigate("/experience-initiatives");
  };

  const handleCancel = () => {
    window.history.back();
  };

  return (
    <PageShell title="Sửa Sáng kiến">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">Sửa Sáng kiến</h1>
        </div>

        {/* Tabs Section */}
        <div className="bg-white rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("initiative-info")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "initiative-info"
                    ? "border-[#337ab7] text-[#337ab7]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Thông tin sáng kiến
              </button>
              <button
                onClick={() => setActiveTab("full-content")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "full-content"
                    ? "border-[#337ab7] text-[#337ab7]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Nội dung đầy đủ
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "initiative-info" && (
              <div className="space-y-8">
                {/* Tên sáng kiến */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên sáng kiến <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.initiativeName}
                    onChange={(e) =>
                      handleInputChange("initiativeName", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên sáng kiến..."
                  />
                </div>

                {/* 2 Columns: Đơn vị quản lý - Lĩnh vực nghiên cứu */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Đơn vị quản lý: <span className="text-red-500">(*)</span>
                    </label>
                    <select
                      value={formData.managingUnit}
                      onChange={(e) =>
                        handleInputChange("managingUnit", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>- Chọn Đơn vị chủ trì -</option>
                      <option>Phòng Khoa học</option>
                      <option>Khoa CNTT</option>
                      <option>Ban Tổng Giám đốc</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lĩnh vực nghiên cứu:{" "}
                      <span className="text-red-500">(*)</span>
                    </label>
                    <select
                      value={formData.researchField}
                      onChange={(e) =>
                        handleInputChange("researchField", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>- Chọn lĩnh vực nghiên cứu -</option>
                      <option>Hệ thống thông tin</option>
                      <option>Khoa học dữ liệu</option>
                      <option>
                        Chiến lược thủy lợi của các vùng, miền và quốc gia
                      </option>
                    </select>
                  </div>
                </div>

                {/* 3 Columns with Người đề xuất spanning 2 columns */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Người đề xuất: <span className="text-red-500">(*)</span>
                    </label>
                    <select
                      value={formData.proposer}
                      onChange={(e) =>
                        handleInputChange("proposer", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>- Chọn người đề xuất -</option>
                      <option>Nguyễn Văn A</option>
                      <option>Trần Thị B</option>
                      <option>Thượng tá, VS. ThS Lê Anh Tuấn</option>
                      <option>VS. ThS Lê Anh Tuấn</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thời gian thực hiện (Tháng):{" "}
                      <span className="text-red-500">(*)</span>
                    </label>
                    <select
                      value={formData.implementationMonth}
                      onChange={(e) =>
                        handleInputChange("implementationMonth", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>- Chọn thời gian -</option>
                      {Array.from(
                        { length: 12 },
                        (_, i) => `${i + 1} Tháng`
                      ).map((m) => (
                        <option key={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Năm thực hiện: <span className="text-red-500">(*)</span>
                    </label>
                    <select
                      value={formData.year}
                      onChange={(e) =>
                        handleInputChange("year", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>- Chọn năm -</option>
                      {Array.from({ length: 11 }, (_, i) => 2020 + i).map(
                        (y) => (
                          <option key={y}>{y}</option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                {/* 2 Columns: Kinh phí dự kiến (VND) - Trạng thái */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kinh phí dự kiến
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        value={formData.estimatedBudget}
                        onChange={(e) =>
                          handleInputChange("estimatedBudget", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-l-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Nhập kinh phí..."
                      />
                      <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 rounded-r-md text-sm bg-gray-50 text-gray-600">
                        (VND)
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trạng thái: <span className="text-red-500">(*)</span>
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        handleInputChange("status", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>- Trạng thái -</option>
                      <option>Mới</option>
                      <option>Đang thực hiện</option>
                      <option>Đạt</option>
                      <option>Không đạt</option>
                    </select>
                  </div>
                </div>

                {/* Nội dung tóm tắt, khả năng áp dụng, đơn vị áp dụng, kết quả dự kiến */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tóm tắt nội dung sáng kiến
                  </label>
                  <textarea
                    value={formData.summary}
                    onChange={(e) =>
                      handleInputChange("summary", e.target.value)
                    }
                    className="w-full h-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Nhập tóm tắt nội dung..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Khả năng áp dụng sáng kiến
                  </label>
                  <textarea
                    value={formData.applicationCapability}
                    onChange={(e) =>
                      handleInputChange("applicationCapability", e.target.value)
                    }
                    className="w-full h-28 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Mô tả khả năng áp dụng..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Đơn vị áp dụng sáng kiến
                  </label>
                  <textarea
                    value={formData.applyingUnit}
                    onChange={(e) =>
                      handleInputChange("applyingUnit", e.target.value)
                    }
                    className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Nhập đơn vị áp dụng..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dự kiến kết quả đạt được
                  </label>
                  <textarea
                    value={formData.expectedOutcome}
                    onChange={(e) =>
                      handleInputChange("expectedOutcome", e.target.value)
                    }
                    className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Nhập kết quả dự kiến..."
                  />
                </div>

                {/* File đính kèm (đồng bộ bài báo) */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      File đính kèm
                    </label>

                    {/* Custom File Input */}
                    <div className="flex items-center space-x-4">
                      <input
                        id="initiative-file-upload"
                        type="file"
                        multiple
                        onChange={handleAttachmentChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="initiative-file-upload"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                      >
                        Choose Files
                      </label>
                      <span className="text-sm text-gray-500">
                        {formData.attachedFiles.length === 0
                          ? "No file chosen"
                          : `${formData.attachedFiles.length} file(s) chosen`}
                      </span>
                    </div>

                    {/* File List */}
                    {formData.attachedFiles.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Files:
                        </h4>
                        <div
                          className={`space-y-2 ${
                            formData.attachedFiles.length > 3
                              ? "max-h-48 overflow-y-auto pr-2"
                              : ""
                          }`}
                        >
                          {formData.attachedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center space-x-2 min-w-0 flex-1">
                                <svg
                                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium text-gray-900 truncate">
                                    {file.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {(file.size / 1024).toFixed(1)} KB
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  const updatedFiles =
                                    formData.attachedFiles.filter(
                                      (_, i) => i !== index
                                    );
                                  handleInputChange(
                                    "attachedFiles",
                                    updatedFiles
                                  );
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors flex-shrink-0"
                                title="Xóa file"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "full-content" && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nội dung đầy đủ
                  </label>
                  <RichTextEditor
                    value={formData.fullContent}
                    onChange={(value: string) =>
                      handleInputChange("fullContent", value)
                    }
                    placeholder="Nhập nội dung đầy đủ..."
                    height="large"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            <span>Hủy</span>
          </button>
          <button
            onClick={handleSave}
            className="bg-green-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <span>Lưu dữ liệu</span>
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default EditInitiative;
